import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { GroupClient } from '@app-services/api/clients/group-settings.client';
import {
  BaseGetByFileIdRequest,
  BaseGetSearchCountByIdRequest,
} from '@app-types/base/base';
import { Group } from '@app-types/api/group';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SearchBarComponent } from '@app-components/common/search-bar/search-bar.component';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DrawerService } from '@app-services/drawer.service';
import { OrganizationGroupCreateDialogComponent } from '@app-components/settings/organization/organization-groups/organization-group-create-dialog/organization-group-create-dialog.component';
import { DatePipe } from '@angular/common';
import { AddGroupsToUsersFormComponent } from '@app-components/settings/organization/organization-groups/add-groups-to-users-form/add-groups-to-users-form.component';
import {
  SETTING_SETTINGS,
  defaultFilter,
} from '@app-shared/constants/constants';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { DeleteDialogComponent } from '@app-components/common/delete-dialog/delete-dialog.component';
import { Subscription } from 'rxjs';
import { SkeletonTableComponent } from '@app-components/common/skeletons/skeleton-table/skeleton-table.component';
import { TableHeaderComponent } from '@app-components/common/table-header/table-header.component';
import { InfinityScrollComponent } from '@app-components/common/infinity-scroll/infinity-scroll.component';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FilterFields, OptionsEnum } from '@app-types/common';
import { GroupSearchOrder } from '@app-types/enums/GroupSearchOrder';
import { FormatGroupsSearchOrders } from '@app-pipes/format-groups-search-orders.pipe';
import { OrderDirection } from '@app-types/enums/order-direction';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';

const { PAGE_SIZE, STEP_FETCH } = SETTING_SETTINGS;

@Component({
  selector: 'app-organization-groups',
  templateUrl: './organization-groups.component.html',
  styleUrls: ['./organization-groups.component.scss'],
  imports: [
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    SearchBarComponent,
    NoDataComponent,
    SomethingWentWrongComponent,
    MatTableModule,
    MatCheckboxModule,
    OrganizationGroupCreateDialogComponent,
    DatePipe,
    AddGroupsToUsersFormComponent,
    MatTooltipModule,
    MatCardModule,
    SkeletonTableComponent,
    TableHeaderComponent,
    InfinityScrollComponent,
    MatSort,
    MatSortModule,
    LoaderComponent,
  ],
})
export class OrganizationGroupsComponent implements OnInit, OnDestroy {
  @HostBinding('class') className = 'setting-container';
  public organizationId: number | undefined;
  public isLoading = true;
  public isError = false;
  public useMobileView = false;
  public searchValue = '';
  public totalSize = 0;
  public canNavigateForward = true;
  public isLoadingScroll = false;
  public isErrorScroll = false;
  public offset = 0;
  public stepFetch = STEP_FETCH;
  public pageSize = PAGE_SIZE;
  isReloadLoading = false;
  private suppressSortDrivenSearch = false;
  public displayedColumns: string[] = [
    'select',
    'name',
    'createdAt',
    'actions',
  ];

  public editingGroup: Group | null;
  public selectGroup: Group | null;
  private useMobileViewSubscription: Subscription;
  public dataSource = new MatTableDataSource<Group>();
  public selection = new SelectionModel<Group>(true, []);
  public searchOrder: GroupSearchOrder | 0 = 0;
  public searchOrders: OptionsEnum[];
  public filter: FilterFields<GroupSearchOrder> = {
    ...defaultFilter,
  };
  public isExporting = false;
  private formatGroupsSearchOrders = new FormatGroupsSearchOrders();
  @ViewChild('createDrawer') createDrawer: TemplateRef<any>;
  @ViewChild('addGroupsToUsersDrawer') addGroupsToUsersDrawer: TemplateRef<any>;
  @ViewChild(InfinityScrollComponent) infinityScroll: InfinityScrollComponent;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private mobileObserverService: MobileObserverService,
    private groupClient: GroupClient,
    private matchError: MatchError,
    public dialog: MatDialog,
    private drawerService: DrawerService,
    private userWorkspaceService: UserWorkspaceService,
    private tempFileClient: TempFileClient
  ) {}

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));

    this.organizationId =
      this.userWorkspaceService.currentOrganization?.organizationId;

    this.searchOrders = this.displayedColumns
      .filter(el => el !== 'actions' && el !== 'select')
      .map(key => ({
        id: this.formatGroupsSearchOrders.transform(key),
        name: key,
      }));

    await this.fetchGroups();
  }

  public async fetchGroups(): Promise<void> {
    if (!this.organizationId) return;
    const loaderTimeout =
      !this.offset && !this.dataSource.data.length
        ? setTimeout(() => {
            this.isLoading = true;
          }, 500)
        : undefined;

    this.offset && (this.isLoadingScroll = true);
    this.isError = false;
    this.isErrorScroll = false;

    try {
      const request = {
        organizationId: this.organizationId,
        offset: this.offset,
        fetch: this.stepFetch,
        filter: this.filter,
        search: this.searchValue ? this.searchValue : null,
      };
      const searchCountReq = new BaseGetSearchCountByIdRequest(
        this.organizationId,
        this.searchValue
      );
      const totalSize = await this.groupClient.getSearchCounter(searchCountReq);
      this.totalSize = totalSize.result;

      const { data } = await this.groupClient.getAllByOrganization(request);
      this.dataSource.data =
        this.offset === 0 ? data : [...this.dataSource.data, ...data];
      this.canNavigateForward = this.totalSize !== this.dataSource.data.length;
      this.drawerService.closeDrawer();
    } catch (e) {
      if (this.isLoadingScroll) {
        this.isErrorScroll = true;
      } else {
        this.isError = true;
      }
      this.matchError.logError(e);
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.isLoading && (this.isLoading = false);
      this.isLoadingScroll && (this.isLoadingScroll = false);
    }
  }

  public onSearchValueChanged(newSearchValue: string): void {
    this.searchValue = newSearchValue;
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach(row => this.selection.select(row));
  }

  public onCreateGroupBtnClicked(): void {
    this.drawerService.openDrawer(this.createDrawer);
  }

  public onCloseCreateUpdateForm(): void {
    this.editingGroup = null;
  }

  public onUpdateGroupBtnClicked(group: Group): void {
    this.editingGroup = group;
    this.onCreateGroupBtnClicked();
  }

  public onAddGroupsToUsersBtnClicked(): void {
    this.drawerService.openDrawer(this.addGroupsToUsersDrawer);
  }

  public onAddGroupToUsersBtnClicked(group: Group): void {
    this.selectGroup = group;
    this.drawerService.openDrawer(this.addGroupsToUsersDrawer);
  }

  public onHideAddGroupsToUsersForm(): void {
    !!this.selectGroup && (this.selectGroup = null);
  }

  public onAddGroupsToUsers(): void {
    !!this.selection.selected?.length && this.selection.clear();
    !!this.selectGroup && (this.selectGroup = null);
  }

  async onDelete(groups: number[]): Promise<void> {
    await this.groupClient.delete({ ids: groups });
  }

  public openConfirmDeleteDialog(group: Group): void {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '450px',
      autoFocus: false,
      data: {
        title: 'groupDeleteTitle',
        subTitles: [
          {
            title: 'groupDelete',
            subTitle: group.group,
          },
        ],
        onDelete: async () => await this.onDelete([group.groupId]),
      },
    });

    dialogRef.afterClosed().subscribe(x => {
      if (x?.isDeleted) {
        this.onSearch();
      }
    });
  }

  public countTo(): string {
    return this.dataSource.data.length.toString();
  }

  public async onScroll(): Promise<void> {
    this.offset = this.offset + this.pageSize;
    await this.fetchGroups();
  }

  async refreshData(): Promise<void> {
    await this.fetchGroups();
  }

  resetScrollData(): void {
    this.offset = 0;
  }

  async reload(): Promise<void> {
    this.isReloadLoading = true;
    this.selection.clear();
    this.filter = { ...defaultFilter };
    this.searchOrder = 0;
    this.suppressSortDrivenSearch = true;
    this.clearSort();
    try {
      await this.onSearch();
    } finally {
      this.isReloadLoading = false;
      setTimeout(() => {
        this.suppressSortDrivenSearch = false;
      }, 0);
    }
  }

  async onSearch(): Promise<void> {
    this.resetScrollData();
    this.infinityScroll?.scrollTop();
    await this.fetchGroups();
  }

  clearSort(): void {
    if (!this.sort) {
      return;
    }
    this.sort.sort({ id: '', start: 'asc', disableClear: false });
    this.sort.active = '';
    this.sort.direction = '';
  }

  async changeOrder(): Promise<void> {
    this.filter.orderDirection =
      this.filter.orderDirection === OrderDirection.Ascending
        ? OrderDirection.Descending
        : OrderDirection.Ascending;
    await this.onSearch();
  }

  public async searchOrderChange($event: any): Promise<void> {
    this.searchOrder = $event as GroupSearchOrder;
    this.filter.searchOrder = this.searchOrder;
    this.filter.orderDirection = OrderDirection.Ascending;
    await this.onSearch();
  }

  async sortData(sort: Sort): Promise<void> {
    if (this.suppressSortDrivenSearch) {
      return;
    }
    this.filter.searchOrder = sort.direction
      ? this.formatGroupsSearchOrders.transform(sort.active)
      : GroupSearchOrder.OrderByGroupId;

    this.filter.orderDirection =
      sort.direction === 'desc'
        ? OrderDirection.Descending
        : OrderDirection.Ascending;

    await this.onSearch();
  }

  async exportExcel(): Promise<void> {
    if (!this.organizationId) return;
    this.isExporting = true;
    try {
      // const request = {
      //   offset: 1,
      //   fetch: 1,
      //   filter: this.filter,
      //   search: this.searchValue,
      //   organizationId: this.organizationId,
      // };

      const fileResponse = await this.groupClient.exportExcel();
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExporting = false;
    }
  }

  async retryData(): Promise<void> {
    await this.fetchGroups();
    setTimeout(() => {
      this.infinityScroll?.reconnect();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.useMobileViewSubscription?.unsubscribe();
  }
}
